/**
 * Responsibility: ease a value toward a target at a rate that does not depend on framerate.
 *
 * Every damped axis in the project routes through here — aircraft roll and pitch, camera
 * position and camera roll — so there is one place where "how does easing work" is decided.
 *
 * ## Why not `current += (target - current) * 0.1`
 *
 * That common form is wrong in a way that is easy to miss: the fraction is per *call*, so
 * the same code eases twice as fast at 120 Hz as at 60 Hz. The simulation runs on a fixed
 * timestep so it would not misbehave today, but the camera is updated per rendered frame
 * and would visibly change character with display refresh rate.
 *
 * The exponential form below is the exact solution to a first-order lag over an interval.
 * It is correct for any `dt`, never overshoots, and never oscillates.
 */

/**
 * Move a value a framerate-independent fraction of the way toward a target.
 *
 * @param {number} current
 * @param {number} target
 * @param {number} dt - Elapsed seconds. Zero or negative leaves the value untouched.
 * @param {number} timeConstant - Seconds to close about 63% of the remaining gap. Larger
 *   is heavier and floatier. Must be positive.
 * @returns {number}
 */
export function damp(current, target, dt, timeConstant) {
  if (!(timeConstant > 0)) {
    throw new Error('damp: timeConstant must be positive');
  }
  if (!(dt > 0)) {
    return current;
  }

  return current + (target - current) * (1 - Math.exp(-dt / timeConstant));
}
